<template>
  <div class="tw-inline-block">
    <button :class="buttonClasses" :title="title" type="button" @click="open">
      <i class="fa fa-history"></i>
      <span v-if="withLabel" class="tw-ml-1">Änderungshistorie</span>
    </button>

    <div v-if="showModal" class="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center">
      <div class="tw-absolute tw-inset-0 tw-bg-black tw-opacity-50" @click="showModal = false"></div>
      <div class="tw-relative tw-bg-white tw-text-gray-900 tw-rounded tw-shadow-lg tw-p-6 tw-w-full tw-max-w-2xl"
           style="max-height: 80vh; overflow-y: auto;">
        <div class="tw-flex tw-justify-between tw-items-start">
          <div>
            <h2 class="tw-text-xl tw-font-bold">Änderungshistorie</h2>
            <p v-if="workDate" class="tw-text-sm tw-text-gray-500 tw-mt-0.5">{{ formattedWorkDate }}</p>
          </div>
          <i class="fa fa-times tw-text-xl tw-cursor-pointer tw-ml-4" @click="showModal = false"></i>
        </div>

        <hr class="tw-my-4">

        <div v-if="loading" class="tw-text-center tw-py-10 tw-text-gray-400">
          <i class="fa fa-spinner fa-spin tw-text-2xl"></i>
        </div>

        <div v-else-if="entries.length === 0" class="tw-text-center tw-py-10 tw-text-gray-400">
          <i class="fa fa-inbox tw-text-3xl tw-mb-2 tw-block"></i>
          Keine Änderungen vorhanden.
        </div>

        <div v-else>
          <div
              v-for="entry in entries"
              :key="entry.id"
              class="tw-mb-4 tw-rounded tw-border tw-border-gray-200 tw-overflow-hidden"
          >
            <!-- Header -->
            <div
                class="tw-flex tw-justify-between tw-items-center tw-px-4 tw-py-2 tw-bg-gray-100 tw-border-b tw-border-gray-200">
              <span class="tw-font-semibold tw-text-sm">{{ entry.work_date_formatted }}</span>
              <span class="tw-text-xs tw-text-gray-500">{{
                  entry.created_at_formatted
                }} &mdash; {{ entry.changed_by_name }}</span>
            </div>

            <!-- Grund -->
            <div class="tw-px-4 tw-py-3 tw-bg-white tw-border-b tw-border-gray-200">
              <p class="tw-text-xs tw-text-gray-500 tw-mb-1 tw-font-medium">Grund</p>
              <p class="tw-text-sm tw-text-gray-800 tw-whitespace-pre-wrap">{{ entry.change_comment }}</p>
            </div>

            <!-- Diff -->
            <template v-if="entry.before_data || entry.after_data">
              <template v-if="computeDiff(entry).length === 0">
                <p class="tw-px-4 tw-py-3 tw-text-xs tw-text-gray-400 tw-italic">Keine inhaltlichen Änderungen
                  erfasst.</p>
              </template>
              <template v-else>
                <div
                    v-for="(diff, di) in computeDiff(entry)"
                    :key="di"
                    class="tw-px-4 tw-py-3 tw-border-t tw-border-gray-200"
                >
                  <!-- Eintrag hinzugefügt -->
                  <template v-if="diff.type === 'added'">
                    <span
                        class="tw-inline-block tw-text-xs tw-font-semibold tw-text-green-700 tw-bg-green-100 tw-px-2 tw-py-0.5 tw-rounded tw-mb-2">
                      + Eintrag {{ diff.index + 1 }} hinzugefügt
                    </span>
                    <p class="tw-text-xs tw-text-gray-700">{{ diff.entry.cost_carrier }} &middot; {{
                        diff.entry.function
                      }} &middot; {{ diff.entry.start }}–{{ diff.entry.end }}</p>
                  </template>

                  <!-- Eintrag entfernt -->
                  <template v-else-if="diff.type === 'removed'">
                    <span
                        class="tw-inline-block tw-text-xs tw-font-semibold tw-text-red-700 tw-bg-red-100 tw-px-2 tw-py-0.5 tw-rounded tw-mb-2">
                      &minus; Eintrag {{ diff.index + 1 }} entfernt
                    </span>
                    <p class="tw-text-xs tw-text-gray-700">{{ diff.entry.cost_carrier }} &middot; {{
                        diff.entry.function
                      }} &middot; {{ diff.entry.start }}–{{ diff.entry.end }}</p>
                  </template>

                  <!-- Felder geändert -->
                  <template v-else>
                    <p class="tw-text-xs tw-text-gray-400 tw-mb-2">Eintrag {{ diff.index + 1 }}</p>
                    <div class="tw-text-xs"
                         style="display: grid; grid-template-columns: 6rem 1fr 1.25rem 1fr; row-gap: 3px; column-gap: 6px;">
                      <div v-for="change in diff.changes" :key="change.field" style="display: contents;">
                        <div class="tw-text-gray-500 tw-self-start">{{ change.field }}</div>
                        <div class="tw-text-red-600 tw-line-through tw-self-start">{{ change.before || '–' }}</div>
                        <div class="tw-text-gray-400 tw-text-center tw-self-start">→</div>
                        <div class="tw-text-green-700 tw-self-start">{{ change.after || '–' }}</div>
                      </div>
                    </div>
                  </template>
                </div>
              </template>
            </template>
          </div>
        </div>

        <div class="tw-flex tw-justify-end tw-mt-4">
          <button class="cu-btn-default" type="button" @click="showModal = false">Schließen</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const FIELDS = [
  {key: 'cost_carrier', label: 'Projekt'},
  {key: 'function', label: 'Gewerk'},
  {key: 'description', label: 'Beschreibung'},
  {key: 'start', label: 'Von'},
  {key: 'end', label: 'Bis'},
];

function formatBreaks(breaks) {
  if (!breaks || breaks.length === 0) return '–';
  return breaks.map(b => `${b.start}–${b.end}`).join(', ');
}

export default {
  name: 'WorktimeHistoryButton',

  props: {
    userId: {
      required: true,
      type: Number,
    },
    workDate: {
      required: false,
      type: String,
      default: null,
    },
    withLabel: {
      required: false,
      type: Boolean,
      default: false,
    },
    buttonClasses: {
      required: false,
      type: String,
      default: 'cu-btn-default',
    },
  },

  data() {
    return {
      showModal: false,
      loading: false,
      entries: [],
    };
  },

  computed: {
    title() {
      return this.workDate ? 'Änderungshistorie für diesen Tag' : 'Komplette Änderungshistorie';
    },
    formattedWorkDate() {
      if (!this.workDate) return null;
      const [year, month, day] = this.workDate.split('-');
      return `${day}.${month}.${year}`;
    },
  },

  methods: {
    open() {
      this.entries = [];
      this.showModal = true;
      this.fetchHistory();
    },

    fetchHistory() {
      this.loading = true;
      const params = this.workDate ? {date: this.workDate} : {};
      window.axios.get(`/worktimes/${this.userId}/change-history`, {params})
          .then(response => {
            this.entries = response.data;
          })
          .finally(() => {
            this.loading = false;
          });
    },

    computeDiff(entry) {
      const before = entry.before_data || [];
      const after = entry.after_data || [];

      if (!entry.before_data && !entry.after_data) return [];

      const result = [];
      const maxLen = Math.max(before.length, after.length);

      for (let i = 0; i < maxLen; i++) {
        const b = before[i] || null;
        const a = after[i] || null;

        if (!b) {
          result.push({type: 'added', index: i, entry: a});
          continue;
        }
        if (!a) {
          result.push({type: 'removed', index: i, entry: b});
          continue;
        }

        const changes = [];

        FIELDS.forEach(({key, label}) => {
          if ((b[key] || '') !== (a[key] || '')) {
            changes.push({field: label, before: b[key], after: a[key]});
          }
        });

        const breaksBefore = formatBreaks(b.breaks);
        const breaksAfter = formatBreaks(a.breaks);
        if (breaksBefore !== breaksAfter) {
          changes.push({field: 'Pausen', before: breaksBefore, after: breaksAfter});
        }

        if (changes.length > 0) {
          result.push({type: 'changed', index: i, changes});
        }
      }

      return result;
    },
  },
};
</script>
